import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'

import MultiSaveButton from './MultiSaveButton'
import { getTripImageFromCheckoutItems } from './utils'
import type { AllCheckoutItemViews } from 'checkout/selectors/view/getAllItemViewsByType'
import getAllItemViewsByType from 'checkout/selectors/view/getAllItemViewsByType'
import { useAppSelector } from 'hooks/reduxHooks'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCartId } from 'selectors/checkoutSelectors'
import type { BookmarkResponse } from 'tripPlanner/api/bookmark/types'
import { useEditableTrips } from 'tripPlanner/hooks/api/trip'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import type { BasicTrip } from 'tripPlanner/types/common'
import { createBookmarkPayloadsFromCheckout } from 'tripPlanner/utils/bookmarks/checkout'
import {
  createTripItemKeysFromBookmarkPayloads,
  getTripItemIdsInCheckout,
  tripHasItemKeys,
} from 'tripPlanner/utils/bookmarks/common'

interface Props {
  checkoutItemViews: AllCheckoutItemViews
  currentTripId?: string
}

function CheckoutSaveButton({ checkoutItemViews, currentTripId }: Props) {
  const intl = useIntl()
  const { data: editableTrips } = useEditableTrips()
  const trips = editableTrips ?? EmptyArray
  const firstOffer = checkoutItemViews.data.accommodationItemsView.data
    .map((view) => view.offer)
    .find((offer) => offer?.type !== 'cruise')

  const cartId = useAppSelector(getCartId)

  const [payloads, keys] = useMemo(() => {
    try {
      const payloads = createBookmarkPayloadsFromCheckout(
        checkoutItemViews,
        cartId,
      )
      const keys = createTripItemKeysFromBookmarkPayloads(payloads)
      return [payloads, keys]
    } catch {
      return [[], []]
    }
  }, [checkoutItemViews, cartId])

  const trip = useMemo(
    () =>
      currentTripId ?
          trips.find((t) => t.id === currentTripId) :
          trips.find((trip) => tripHasItemKeys(trip, keys)),
    [currentTripId, keys, trips],
  )

  const newTripName = useNewTripNameFromOffer(firstOffer)

  const savedItemIds = useMemo(
    () => getTripItemIdsInCheckout(keys, trip?.plannedIdSets ?? []),
    [trip, keys],
  )

  const newTripImageId = useMemo(
    () => getTripImageFromCheckoutItems(checkoutItemViews, trips),
    [checkoutItemViews, trips],
  )

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const onSaveSuccess = useCallback(
    (data: BookmarkResponse['result'], trip: BasicTrip) => {
      showSaveSuccessSnackbar(trip.id, trip.name, data.created[0].id)
    },
    [showSaveSuccessSnackbar],
  )

  const onRemoveSuccess = useCallback(
    (trip: BasicTrip) => {
      showRemoveSuccessSnackbar(trip.id, trip.name)
    },
    [showRemoveSuccessSnackbar],
  )

  const saveText = intl.formatMessage({
    id: 'jrx_FJxuytzhaq0jHtee',
    defaultMessage: 'Save',
    description: 'Short button label to save checkout item to a trip on mobile and tablet',
  })
  const saveToTripText = intl.formatMessage({
    id: 'TE6LIRgnNDVdjyQH24Pv',
    defaultMessage: 'Save to trip',
    description: 'Button label to save checkout item to a trip on desktop',
  })

  const saveLabel = useResponsiveSelector({
    mobile: saveText,
    tablet: saveText,
    desktop: saveToTripText,
    largeDesktop: saveToTripText,
    extraLargeDesktop: saveToTripText,
  })

  if (payloads.length === 0) {
    return null
  }

  return (
    <MultiSaveButton
      focusedTrip={trip}
      newTripImageId={newTripImageId}
      newTripName={newTripName}
      keys={keys}
      onRemoveError={showRemoveErrorSnackbar}
      onRemoveSuccess={onRemoveSuccess}
      onSaveError={showSaveErrorSnackbar}
      onSaveSuccess={onSaveSuccess}
      payloads={payloads}
      saveLabel={saveLabel}
      savedItemIds={savedItemIds}
      trips={trips}
    />
  )
}

function mapStateToProps(state: App.State) {
  return {
    checkoutItemViews: getAllItemViewsByType(state),
    currentTripId: state.tripPlanner.currentTripId,
  }
}

export default connect(mapStateToProps)(CheckoutSaveButton)
